import { KeyboardArrowDown } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Link,
  Typography,
} from '@mui/material';

export const DemoInstructions = () => {
  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Instructions
      </Typography>

      <Accordion>
        <AccordionSummary expandIcon={<KeyboardArrowDown />}>
          <Typography>How to log in as a coach or member</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={1.5}>
            <Box>
              <Typography variant="subtitle2">Log in as the coach</Typography>
              <Typography variant="body2" color="text.secondary">
                Copy the coach&apos;s email above (or open their profile with
                View), then sign into{' '}
                <Link
                  href="https://coach.dev.prince.ex-os.net"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  dev Coach Hub
                </Link>{' '}
                with that email and the test password.
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2">Log in as a member</Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                component="div"
              >
                <ol style={{ margin: 0, paddingLeft: '1.25rem' }}>
                  <li>
                    <strong>
                      Point your device at the dev environment first.
                    </strong>{' '}
                    These demo accounts only exist in dev, so the normal
                    production app can&apos;t see them. In the app:
                    <ol
                      style={{ marginTop: '0.25rem', paddingLeft: '1.25rem' }}
                    >
                      <li>
                        Tap the bug/beaker/hammer icon in the top-right corner,
                        next to your profile. (If you do not see it, request it
                        from the Engineering team)
                      </li>
                      <li>
                        Scroll all the way down and tap the{' '}
                        <strong>environment override</strong> button.
                      </li>
                      <li>Select "Logout"</li>
                      <li>
                        Select <strong>dev</strong> and tap{' '}
                        <strong>Apply</strong>.
                      </li>
                    </ol>
                    The override sticks for that device until you change it
                    back, so you only need to do this once per device. Otherwise
                    login fails with &quot;member not found.&quot;
                  </li>
                  <li>
                    Expand the client&apos;s demo members and click a
                    member&apos;s email to copy it.
                  </li>
                  <li>Sign in with that email and the test password.</li>
                </ol>
              </Typography>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<KeyboardArrowDown />}>
          <Typography>Coach email requirements</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={1.5}>
            <Typography variant="body2" color="text.secondary">
              Any coach email you enter must use the{' '}
              <strong>@testuser.ex-os.net</strong> domain (e.g.{' '}
              <code>yourname@testuser.ex-os.net</code>). Other domains can hit
              SSO and fail to log in as a demo coach. Leave the field blank to
              have one generated for you automatically.
            </Typography>
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<KeyboardArrowDown />}>
          <Typography>Demo (predefined) vs stub members</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={1.5}>
            <Box>
              <Typography variant="subtitle2">
                Demo (predefined) members
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                component="div"
              >
                <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                  <li>
                    ~9 rich personas per client (Sr. Leader, High Growth, Young
                    Employee, Shift Worker).
                  </li>
                  <li>
                    Full, realistic profiles plus seeded history: sleep, steps,
                    activities, challenges, coach chats, notes, training plans,
                    gameplans.
                  </li>
                  <li>
                    Use for feature demos that need real data. Slower to create.
                  </li>
                </ul>
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2">Stub members</Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                component="div"
              >
                <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                  <li>
                    500 lightweight placeholder members per client — names on
                    the roster only, minimal data.
                  </li>
                  <li>
                    Use to show scale (roster size, list density). Fast to
                    create.
                  </li>
                </ul>
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              <strong>Short version:</strong> predefined = few, deep, realistic.
              Stub = many, shallow, filler.
            </Typography>

            <Typography variant="subtitle2" sx={{ mt: 1 }}>
              What each demo member comes with
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Every demo (predefined) member is pre-loaded, so when you log in
              as one you can immediately show a populated experience:
            </Typography>
            <Typography variant="body2" color="text.secondary" component="div">
              <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                <li>
                  <strong>Wearable sleep history</strong> — ~6 weeks (≈6.5 hr
                  average with a couple of low nights) plus an irregular resting
                  heart rate.
                </li>
                <li>
                  <strong>Step &amp; walk activity</strong> — several
                  7,000–10,000 step days each week.
                </li>
                <li>
                  <strong>Challenge participation</strong> — joined and
                  progressing on the distance, steps, duration, strength, and
                  all-rounder challenges.
                </li>
                <li>
                  <strong>Coach &amp; member chat</strong> — a scripted
                  conversation (gameplan summary, resource links, member
                  replies) that varies by gender and working style.
                </li>
                <li>
                  <strong>Coaching note</strong> — a session note on file.
                </li>
                <li>
                  <strong>Training plan</strong> — matched to the client (Full
                  Gym, Dumbbell-only, or Bodyweight).
                </li>
                <li>
                  <strong>Gameplan in progress</strong> — preassessment
                  completed with the first several plan items already done.
                </li>
              </ul>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Profiles also vary by persona (Sr. Leader, High Growth, Young
              Employee, Shift Worker) with realistic age, job title, focus area,
              and working style — handy for showing personalization.
            </Typography>
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<KeyboardArrowDown />}>
          <Typography>How to reset / delete members</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={1.5}>
            <Box>
              <Typography variant="subtitle2">Resetting</Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                component="div"
              >
                <ol style={{ margin: 0, paddingLeft: '1.25rem' }}>
                  <li>
                    On the client row, any member count above 0 shows a red
                    <strong> clear members</strong> button. Click it to delete
                    that client&apos;s predefined and/or stub members.
                  </li>
                  <li>
                    Watch the activity log on the right — it shows progress as
                    members are deleted. Leave the page open and don&apos;t
                    click again while it runs.
                  </li>
                  <li>
                    To fully reset, clear both predefined and stub members on
                    all three clients.
                  </li>
                  <li>
                    When it finishes, the counts update on their own; hit
                    <strong> Refresh</strong> if you want to re-confirm they are
                    0.
                  </li>
                </ol>
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2">How long it takes</Typography>
              <Typography variant="body2" color="text.secondary">
                Deletion runs in batches and depends on the count. Predefined
                demo members are removed one at a time, so even though there are
                only ~9 per client it is not instant. Stub members (500 per
                client) are deleted in batches and take the longest — expect
                anywhere from several seconds to a couple of minutes per client.
                It is done when the activity log stops and the counts hit 0.
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2">
                If deletion errors out
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Deletion depends on third-party services and can occasionally
                fail partway. If you see an error, just clear again — it is safe
                to re-run. If a create previously errored, clear that
                client&apos;s members and recreate them.
              </Typography>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};
